import React, { useState, useRef, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import SaveButton from "../Button/SaveButton";
import { FaChevronDown, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useTranslation } from "react-i18next";

const DateFilter = ({
  defaultDate = null, // { startDate, endDate }
  onChange,
  customMode = "range",
  availableFilters = [
    "default",
    "today",
    "yesterday",
    "lastWeek",
    "last15",
    "lastMonth",
    "thisMonth",
    "custom",
  ],
}) => {
  // console.log("default value",defaultDate )
  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const [filter, setFilter] = useState(availableFilters[0]);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const { t } = useTranslation();

  const today = new Date();
  const formatDate = (date) => date.toISOString().split("T")[0];

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-");
    return `${d}-${m}-${y}`;
  };

  const quickFilters = [
    { label: "Default", value: "default" },
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "Last Week", value: "lastWeek" },
    { label: "Last 15 Days", value: "last15" },
    { label: "Last Month", value: "lastMonth" },
    { label: "This Month", value: "thisMonth" },
    { label: "Custom Date", value: "custom" },
  ].filter((f) => availableFilters.includes(f.value));

  const didInit = useRef(false);

  // ================= INIT EFFECT =================
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    if (defaultDate?.startDate) {
      const start = defaultDate.startDate;
      const end = customMode === "single" ? start : defaultDate.endDate || start;

      setFilter("custom");
      setCustomStart(start);
      setCustomEnd(end);
      onChange({ startDate: start, endDate: end });
    } else {
      if (customMode === "single") {
        // single mode always defaults to today
        const todayStr = formatDate(today);
        setFilter("custom");
        setCustomStart(todayStr);
        setCustomEnd(todayStr);
        onChange({ startDate: todayStr, endDate: todayStr });
      } else {
        // financial year default
        const currentYear = today.getFullYear();
        const april = new Date(`${currentYear}-04-01`);
        const startYear = today >= april ? currentYear : currentYear - 1;
        const start = formatDate(new Date(`${startYear}-04-01`));
        const end = formatDate(today);

        setFilter("default");
        setCustomStart(start);
        setCustomEnd(end);
        onChange({ startDate: start, endDate: end });
      }
    }
  }, []);

  // ================= FILTER HANDLER =================
  const handleSelect = (value, initial = false) => {
    setFilter(value);

    if (value === "custom" && !initial) return;

    let start = new Date();
    let end = new Date();

    switch (value) {
      case "default": {
        const currentYear = today.getFullYear();
        const april = new Date(`${currentYear}-04-01`);
        const startYear = today >= april ? currentYear : currentYear - 1;
        start = new Date(`${startYear}-04-01`);
        end = today;
        break;
      }
      case "today":
        start = today;
        end = today;
        break;
      case "yesterday":
        start.setDate(today.getDate() - 1);
        end.setDate(today.getDate() - 1);
        break;
      case "lastWeek":
        start.setDate(today.getDate() - 7);
        break;
      case "last15":
        start.setDate(today.getDate() - 15);
        break;
      case "lastMonth":
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case "thisMonth":
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      default:
        break;
    }

    const result = {
      startDate: formatDate(start),
      endDate: formatDate(end),
    };

    setCustomStart(result.startDate);
    setCustomEnd(result.endDate);
    onChange(result);

    if (!initial) setIsOpen(false);
  };

  // ================= ARROW HANDLER (SINGLE MODE) =================
  const shiftDate = (dateStr, days) => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + days);
    return formatDate(d);
  };

  const handleArrow = (direction) => {
    const delta = direction === "next" ? 1 : -1;
    const baseDate = customStart || formatDate(today);
    const newDate = shiftDate(baseDate, delta);

    setFilter("custom");
    setCustomStart(newDate);
    setCustomEnd(newDate);
    onChange({ startDate: newDate, endDate: newDate });
  };

  // ================= CUSTOM APPLY =================
  const handleCustomApply = () => {
    if (!customStart) return;

    const result =
      customMode === "single"
        ? { startDate: customStart, endDate: customStart }
        : { startDate: customStart, endDate: customEnd || customStart };

    setFilter("custom");
    onChange(result);
    setIsOpen(false);
  };

  // ================= OUTSIDE CLICK =================
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ================= LABEL LOGIC =================
  const isToday = (dateStr) => dateStr === formatDate(today);

  const selectedLabel = (() => {
    if (filter === "custom" && customStart) {
      if (customMode === "single") {
        return isToday(customStart) ? "Today" : formatDisplayDate(customStart);
      }

      return customEnd
        ? `${isToday(customStart) ? "Today" : formatDisplayDate(customStart)} → ${
            isToday(customEnd) ? "Today" : formatDisplayDate(customEnd)
          }`
        : isToday(customStart)
        ? "Today"
        : formatDisplayDate(customStart);
    }

    if (filter === "default") {
      const currentYear = today.getFullYear();
      const april = new Date(`${currentYear}-04-01`);
      const startYear = today >= april ? currentYear : currentYear - 1;
      const start = formatDate(new Date(`${startYear}-04-01`));
      return `${formatDisplayDate(start)} → Today`;
    }

    const current = quickFilters.find((f) => f.value === filter);
    if (current) return current.label;

    return quickFilters[0]?.label || "Select";
  })();

  // ================= UI =================
  return (
    <div ref={dropdownRef} className="relative flex items-center gap-1 text-sm">
      {customMode === "single" && (
        <div
          className="border-1 border-theme-headerBg rounded-1 p-2 hover:bg-theme-dropdownHeqadibg hover:text-theme-color text-theme-headerBg"
          onClick={() => handleArrow("prev")}
        >
          <FaChevronLeft className="cursor-pointer text-[12px] hover:scale-110 transition" />
        </div>
      )}

      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`${
          customMode === "range" ? "w-[200px]" : "w-[130px]"
        } text-left px-2 py-[4px] border-1 border-theme-headerBg rounded-md bg-theme-tableBg text-theme-headerBg hover:bg-theme-tableHoverColor focus:outline-none flex justify-between items-center`}
      >
        <span className="truncate">{selectedLabel}</span>
        <FaChevronDown
          className={`text-[12px] transition-transform ${
            isOpen ? "rotate-180" : "rotate-0"
          }`}
        />
      </button>

      {customMode === "single" && (
        <div
          className="border-1 border-theme-headerBg rounded-1 p-2 hover:bg-theme-dropdownHeqadibg hover:text-theme-color text-theme-headerBg"
          onClick={() => handleArrow("next")}
        >
          <FaChevronRight className="cursor-pointer text-[12px] hover:scale-110 transition" />
        </div>
      )}

      {isOpen && (
        <div
          className={`absolute top-[110%] right-0 z-50 bg-white border border-gray-300 rounded-md shadow-lg flex gap-3 p-2 ${
            filter === "custom"
              ? "flex-row-reverse w-[350px]"
              : "flex-col w-[180px]"
          }`}
        >
          <div
            className={`flex flex-col gap-1 w-[160px] ${
              filter === "custom" ? "border-l border-gray-200 pl-2" : "border-none"
            }`}
          >
            {quickFilters.map((item) => (
              <button
                key={item.value}
                onClick={() => handleSelect(item.value)}
                className={`text-left px-2 py-1 rounded-1 transition-all duration-150 ${
                  filter === item.value
                    ? "bg-theme-dropdownHeqadibg text-white font-semibold"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {filter === "custom" && (
            <div className="flex flex-col text-center gap-2 min-w-[160px]">
              <label className="text-sm font-medium bg-theme-headerBg p-1 rounded-1 text-white">
                {t("selectDate")}
              </label>

              <input
                type="date"
                value={customStart}
                onChange={(e) => {
                  setCustomStart(e.target.value);
                  if (customMode === "single") setCustomEnd(e.target.value);
                }}
                className="form-control form-control-sm"
              />

              {customMode === "range" && (
                <>
                  <div className="text-gray-500 text-sm">to</div>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="form-control form-control-sm"
                  />
                </>
              )}

              <SaveButton onClick={handleCustomApply} text="Apply" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DateFilter;
